import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products, addToCart } = useAppContext();
  const product = products.find(p => p.id === id);
  
  const [color, setColor] = useState('Matte Black');
  const [customText, setCustomText] = useState('');

  if (!product) return <div className="p-12 text-center text-xl font-medium">Product not found</div>;

  const handleStripeBuy = () => {
    if (product.stripe_checkout_url) {
      window.open(product.stripe_checkout_url, '_blank');
    }
  };

  const handleAddToCart = () => {
    addToCart(product, { color, customText });
    navigate('/cart');
  };

  const colors = [
    { name: 'Matte Black', class: 'bg-gray-900' },
    { name: 'Arctic White', class: 'bg-white border-2 border-gray-200' },
    { name: 'Navy Blue', class: 'bg-blue-900' },
  ];

  const hasStripe = !!product.stripe_checkout_url;
  const isCustomizable = product.is_customizable !== false;

  return (
    <div className="py-12 bg-white min-h-[80vh]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          {/* Product Image */}
          <div className="rounded-3xl overflow-hidden bg-gray-100 aspect-square relative shadow-inner">
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
            {isCustomizable && customText && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-white text-2xl font-bold opacity-80 mix-blend-overlay uppercase tracking-widest px-4 text-center">
                  {customText}
                </span>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="mb-6">
              <span className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-2 block">{product.type}</span>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{product.name}</h1>
              <p className="text-2xl font-medium text-gray-900">${product.price}</p>
            </div>
            
            <p className="text-gray-600 mb-8 pb-8 border-b border-gray-100 leading-relaxed">
              {product.description}
            </p>

            {isCustomizable && (
              <div className="space-y-8 mb-8">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Select Color</h3>
                  <div className="flex gap-4">
                    {colors.map(c => (
                      <button
                        key={c.name}
                        onClick={() => setColor(c.name)}
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${c.class} ${
                          color === c.name ? 'ring-4 ring-indigo-200 ring-offset-2 scale-110' : ''
                        }`}
                        title={c.name}
                      >
                        {color === c.name && (
                          <SafeIcon name="Check" className={c.name === 'Arctic White' ? 'text-gray-900' : 'text-white'} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">Custom Print (Optional)</h3>
                  <input
                    type="text"
                    maxLength={20}
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    placeholder="e.g. Your Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                  <p className="text-[10px] text-gray-400 mt-2">Maximum 20 characters.</p>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {hasStripe && (
                <Button size="lg" className="w-full py-4 text-lg rounded-2xl bg-indigo-600 shadow-lg shadow-indigo-100" onClick={handleStripeBuy}>
                  <SafeIcon name="CreditCard" className="mr-2" /> Buy Now with Stripe
                </Button>
              )}
              <Button size="lg" variant="secondary" className="w-full py-4 text-lg rounded-2xl" onClick={handleAddToCart}>
                <SafeIcon name="ShoppingCart" className="mr-2" /> {isCustomizable ? 'Add to Cart' : 'Buy Now'}
              </Button>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-2xl flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-indigo-600 shadow-sm">
                  <SafeIcon name="Truck" />
                </div>
                <span className="text-xs font-bold text-gray-700">Free Shipping</span>
              </div>
              <div className="p-4 bg-gray-50 rounded-2xl flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-indigo-600 shadow-sm">
                  <SafeIcon name="Shield" />
                </div>
                <span className="text-xs font-bold text-gray-700">Lifetime Warranty</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
